import { FirmDatabase } from '@/database/firmDatabase';
import { InsuranceCaseData } from '@shared/types';
import { FormInsuranceCase, InsuranceFormData } from '../types';
import { telemetry } from '@/lib/telemetry';
import { isFirestoreAlreadyExistsError } from '@/utils/firestoreErrors';

export const addOrUpdateInsuranceCases = async (props: {
  firmDb: FirmDatabase;
  formData: InsuranceFormData;
  matterId: string;
  onSubmitEventId: string;
}) => {
  const { firmDb, formData, matterId, onSubmitEventId } = props;

  telemetry.info({
    message: 'addOrUpdateInsuranceCases: Will call firmDb.getInsuranceCases',
    extra: {
      matterId,
      onSubmitEventId,
    },
  });
  const casesSnapshot = await firmDb.getInsuranceCases([
    ['matterId', '==', matterId],
  ]);
  telemetry.info({
    message:
      'addOrUpdateInsuranceCases: Did call firmDb.getInsuranceCases successfully',
    extra: {
      matterId,
      onSubmitEventId,
    },
  });
  // Add or update cases that are in the form
  for (const insuranceCase of formData.insuranceCases) {
    const caseData = caseDataForFormData({ formData, insuranceCase, matterId });
    const existingCase = casesSnapshot.docs.find(
      (doc) => doc.id === insuranceCase.firestoreId,
    );

    if (existingCase) {
      telemetry.info({
        message:
          'addOrUpdateInsuranceCases: Will call firmDb.updateInsuranceCase',
        extra: {
          matterId,
          caseId: existingCase.id,
          caseData,
          onSubmitEventId,
        },
      });
      await firmDb.updateInsuranceCase(existingCase.id, caseData);
      telemetry.info({
        message:
          'addOrUpdateInsuranceCases: Did call firmDb.updateInsuranceCase successfully',
        extra: {
          matterId,
          caseId: existingCase.id,
          caseData,
          onSubmitEventId,
        },
      });
    } else {
      telemetry.info({
        message: 'addOrUpdateInsuranceCases: Will call firmDb.addInsuranceCase',
        extra: {
          matterId,
          caseData,
          onSubmitEventId,
        },
      });
      await addInsuranceCaseWithIdempotencyRecovery({
        firmDb,
        caseData: {
          ...caseData,
          _idempotency: { onSubmitEventId },
        } as InsuranceCaseData,
        matterId,
        onSubmitEventId,
      });
      telemetry.info({
        message:
          'addOrUpdateInsuranceCases: Did call firmDb.addInsuranceCase successfully',
        extra: {
          matterId,
          caseData,
          onSubmitEventId,
        },
      });
    }
  }

  const formCaseIds = formData.insuranceCases.map(
    (insuranceCase) => insuranceCase.firestoreId,
  );

  // Delete any cases that are no longer in the form
  for (const snapshotCase of casesSnapshot.docs) {
    if (!formCaseIds.includes(snapshotCase.id)) {
      telemetry.info({
        message:
          'addOrUpdateInsuranceCases: Will call firmDb.deleteInsuranceCase',
        extra: {
          matterId,
          caseId: snapshotCase.id,
          onSubmitEventId,
        },
      });
      await firmDb.deleteInsuranceCase(snapshotCase.id);
      telemetry.info({
        message:
          'addOrUpdateInsuranceCases: Did call firmDb.deleteInsuranceCase successfully',
        extra: {
          matterId,
          caseId: snapshotCase.id,
          onSubmitEventId,
        },
      });
    }
  }
};

const caseDataForFormData = (props: {
  formData: InsuranceFormData;
  insuranceCase: FormInsuranceCase;
  matterId: string;
}) => {
  const { formData, insuranceCase, matterId } = props;

  const caseData: InsuranceCaseData = {
    provider: formData.insuranceProvider,
    matterId,
    caseType: insuranceCase.caseType ?? undefined,
    caseId: insuranceCase.caseId ?? undefined,
    memberId: insuranceCase.memberId ?? undefined,
  };
  if (insuranceCase.caseValue) {
    const num = Number(insuranceCase.caseValue);
    caseData.caseValue = isNaN(num) ? undefined : num;
  }

  return caseData;
};

const addInsuranceCaseWithIdempotencyRecovery = async (props: {
  firmDb: FirmDatabase;
  caseData: InsuranceCaseData;
  matterId: string;
  onSubmitEventId: string;
}) => {
  const { firmDb, caseData, matterId, onSubmitEventId } = props;

  try {
    return await firmDb.addInsuranceCase(caseData);
  } catch (error) {
    if (!isFirestoreAlreadyExistsError(error)) {
      throw error;
    }

    const matchingCases = await firmDb.getInsuranceCases([
      ['matterId', '==', matterId],
      [
        '_idempotency.onSubmitEventId' as keyof InsuranceCaseData,
        '==',
        onSubmitEventId,
      ],
    ]);

    if (matchingCases.empty) {
      telemetry.error({
        error,
        message:
          'addOrUpdateInsuranceCases: addInsuranceCase returned already-exists, but no case was found by onSubmitEventId lookup',
        extra: {
          matterId,
          onSubmitEventId,
          matchingCaseCount: matchingCases.size,
          addInsuranceCaseError: String(error),
          caseData,
        },
      });
      throw error;
    }

    const exactMatches = matchingCases.docs.filter((doc) =>
      isSameInsuranceCaseData(doc.data(), caseData),
    );

    if (exactMatches.length === 0) {
      telemetry.error({
        error,
        message:
          'addOrUpdateInsuranceCases: addInsuranceCase returned already-exists, but no exact matching case was found during recovery',
        extra: {
          matterId,
          onSubmitEventId,
          matchingCaseCount: matchingCases.size,
          addInsuranceCaseError: String(error),
          caseData,
        },
      });
      throw error;
    }

    const recoveredCaseRef = exactMatches[0].ref;
    telemetry.warn({
      message:
        'addOrUpdateInsuranceCases: addInsuranceCase returned already-exists and was recovered by onSubmitEventId lookup',
      extra: {
        matterId,
        onSubmitEventId,
        recoveredCaseId: recoveredCaseRef.id,
        matchingCaseCount: matchingCases.size,
        exactMatchCount: exactMatches.length,
        addInsuranceCaseError: String(error),
      },
    });
    return recoveredCaseRef;
  }
};

const isSameInsuranceCaseData = (
  left: InsuranceCaseData,
  right: InsuranceCaseData,
) =>
  left.provider === right.provider &&
  left.matterId === right.matterId &&
  left.caseType === right.caseType &&
  left.caseId === right.caseId &&
  left.memberId === right.memberId &&
  left.caseValue === right.caseValue &&
  left.otherInsuranceProviderName === right.otherInsuranceProviderName;
